/**
 * Transform functions cho Travel Map Admin API
 * Chuyển đổi data giữa CMS API format và admin-px format
 */

import type {
  AdminPlace,
  CreatePlaceRequest,
  PlacePhoto,
  UpdatePlaceRequest,
} from '../types';

/**
 * Transform CMS API photos (array<string>) sang PlacePhoto[]
 * order bắt đầu từ 1 (ảnh đầu tiên là ảnh đại diện)
 */
export function transformPhotos(photos: string[] | null | undefined): PlacePhoto[] {
  if (!photos || !Array.isArray(photos)) return [];
  return photos.map((url, index) => ({
    id: `photo-${index}`,
    url,
    order: index + 1,
  }));
}

/**
 * Transform CMS API response sang format admin-px
 * Nếu photos rỗng nhưng có thumbnail, thêm thumbnail vào photos
 */
export function transformCmsPlace(cmsPlace: any): AdminPlace {
  let photos = transformPhotos(cmsPlace.photos);
  const thumbnail = cmsPlace.thumbnail || null;

  // Nếu photos rỗng nhưng có thumbnail, thêm thumbnail vào photos
  if (photos.length === 0 && thumbnail) {
    photos = [{ id: 'photo-thumbnail', url: thumbnail, order: 1 }];
  }

  return {
    id: cmsPlace.id,
    displayName: cmsPlace.displayName,
    formattedAddress: cmsPlace.formattedAddress || '',
    location: cmsPlace.location || { latitude: 0, longitude: 0 },
    category: cmsPlace.category,
    thumbnail,
    internationalPhoneNumber: cmsPlace.internationalPhoneNumber || null,
    websiteUri: cmsPlace.websiteUri || null,
    editorialSummary: cmsPlace.editorialSummary || null,
    regularOpeningHours: cmsPlace.regularOpeningHours || null,
    photos,
    status: cmsPlace.status === 'PUBLISHED' ? 'active' : 'draft',
    isActive: cmsPlace.status === 'PUBLISHED',
    createdAt: cmsPlace.createdAt || new Date().toISOString(),
    updatedAt: cmsPlace.updatedAt || new Date().toISOString(),
  };
}


/**
 * Transform admin-px status sang CMS status
 * admin-px: 'draft' | 'active'
 * CMS: 'DRAFT' | 'PUBLISHED'
 */
export function transformStatus(status?: string): string {
  if (status === 'active') return 'PUBLISHED';
  return 'DRAFT';
}

/**
 * Transform admin-px request sang CMS API format
 * Chỉ gửi các field có giá trị, không gửi undefined
 */
export function transformToCreateRequest(
  data: CreatePlaceRequest | UpdatePlaceRequest,
): Record<string, any> {
  const result: Record<string, any> = {};

  // Required fields
  if (data.displayName !== undefined) result.displayName = data.displayName;
  if (data.location !== undefined) result.location = data.location;
  if (data.category !== undefined) result.category = data.category;

  // Optional fields - chỉ gửi nếu có giá trị
  if (data.formattedAddress !== undefined) result.formattedAddress = data.formattedAddress;
  if (data.internationalPhoneNumber !== undefined)
    result.internationalPhoneNumber = data.internationalPhoneNumber;
  if (data.websiteUri !== undefined) result.websiteUri = data.websiteUri;
  if (data.editorialSummary !== undefined) result.editorialSummary = data.editorialSummary;
  if (data.thumbnail !== undefined) result.thumbnail = data.thumbnail;
  if (data.status !== undefined) result.status = transformStatus(data.status);
  if (data.regularOpeningHours !== undefined) result.regularOpeningHours = data.regularOpeningHours;
  if (data.photos !== undefined) result.photos = data.photos.map((p) => p.url);

  return result;
}

/**
 * Parse response từ umi request
 * Response có thể đã unwrap hoặc chưa
 */
export function parseApiResponse<T>(response: any): { data: T | null; isSuccess: boolean; message: string } {
  // Case 1: { success, message, data } - chưa unwrap
  if (response?.success !== undefined) {
    return {
      data: response.data,
      isSuccess: response.success,
      message: response.message || 'Success',
    };
  }

  // Case 2: Array - đã unwrap (list response)
  if (Array.isArray(response)) {
    return {
      data: response as T,
      isSuccess: true,
      message: 'Success',
    };
  }

  // Case 3: Object với id - đã unwrap (detail response)
  if (response?.id) {
    return {
      data: response as T,
      isSuccess: true,
      message: 'Success',
    };
  }

  // Case 4: Object với data field
  if (response?.data) {
    return {
      data: response.data,
      isSuccess: true,
      message: response.message || 'Success',
    };
  }

  return {
    data: null,
    isSuccess: false,
    message: 'Unknown response format',
  };
}
